import React, { useState, useMemo, useCallback } from 'react';
import type {
  GridColDef,
  GridRowDef,
  DropDownMenuProps,
  DropdownMenuItem,
  ToolbarProps
} from '@p81-common/ui';
import { defaultNavigation } from '../components/CS/CloudManagementLogs/helpers/configurates';
import isEqual from 'lodash/isEqual';
import { defaultNumOfRowsOptions } from 'utils/settings.js';

export interface ApiResponse {
  page: number;
  totalPages: number;
  totalRecords: number;
  totalGwCount: number;
  data: GridRowDef[];
}

export interface Filter {
  fieldName: string;
  direction: number;
  currentPage: number;
  itemsPerPage: number;
  search: string;
  action: never[];
  status: never[];
  updatedByName: never[];
  datetime: {
    startDate: null;
    endDate: null;
  };
}

export const useDataGrid = (
  tableConfiguration: GridColDef[],
  defaultFilters: any
) => {
  const [tableConf, setTableConf] = useState(
    JSON.parse(JSON.stringify(tableConfiguration))
  );
  const [originalTableSettingsListItems] = useState(
    tableConfiguration.slice(1).map(item => ({
      name: item.headerName || item.title,
      selected: true,
      hidden: item.field === 'actions',
      checkboxProps: { disabled: item.field === 'actions' }
    })) as DropdownMenuItem[]
  );
  const [tableSettingsListItems, setTableSettingsListItems] = useState(
    originalTableSettingsListItems
  );
  const [tableSettingsDisplay, setTableSettingsDisplay] = useState(
    tableSettingsListItems
  );
  const [numOfRowsOptions, setNumOfRowsOptions] = useState(
    defaultNumOfRowsOptions || []
  );
  const [filter, setFilter] = useState<Filter>({
    ...defaultFilters,
    ...defaultNavigation
  });
  const [filterBackend, setFilterBackend] = useState({
    key: 'clManLogFilter',
    value: []
  });
  const [visibleFilterSave, setVisibleFilterSave] = useState<boolean | string>(
    false
  );
  const [prevStateTableConf, setPrevStateTableConf] = useState<GridColDef[]>(
    []
  );
  const [triggerSaveTableSettings] = useState(false);

  const [results, setResults] = useState<ApiResponse>();
  const [tableData, setTableData] = useState<any[]>([]);
  const [loaderTable, setLoaderTable] = useState(false);
  const [direction, setDirection] = useState(-1);

  const [startFilter, setStartFilter] = useState<boolean>(false);
  const [filterButton, setFilterButton] = useState(false);
  const [visibleFilterEdit, setVisibleFilterEdit] = useState(false);
  const [changeFilter, setChangeFilter] = useState('Default');
  const [trigerResetDatePicker, setTrigerResetDatePicker] = useState(false);
  const [filters, setFilters] = useState<Filter | {}>({});
  const [loader, setLoader] = useState(true);
  const [dataConection, setDataConection] = useState(true);
  const [selectLoader, setSelectLoader] = useState(false);
  const [prevStateFilter, setPrevStateFilter] = useState<Filter>();
  const [triggerStartEffect, setTriggerStartEffect] = useState(false);

  const arrayMove = <T extends unknown[]>(
    array: T,
    from: number,
    to: number
  ) => {
    const newArray = [...array];
    const startIndex = from < 0 ? array.length + from : from;

    if (startIndex >= 0 && startIndex < array.length) {
      const endIndex = to < 0 ? array.length + to : to;

      const [item] = newArray.splice(from, 1);
      newArray.splice(endIndex, 0, item);
    }

    return newArray;
  };

  const tableConfigDisplay = useMemo(
    () => [
      tableConfiguration[0],
      ...tableSettingsListItems.reduce((acc, item) => {
        if (item.selected) {
          const newItem = tableConfiguration.find(
            i => (i.headerName || i.title) === item.name
          );
          if (newItem) {
            return acc.concat(newItem);
          }
        }
        return acc;
      }, [] as typeof tableConfiguration)
    ],
    [tableConfiguration, tableSettingsListItems]
  );

  const handleFilterInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value?.length > 2) {
      setFilter({ ...filter, search: e.target.value, currentPage: 1 });
      setStartFilter(!startFilter);
    }
  };

  // Value-based search applier: re-apply a persisted search on region switch
  // without constructing a synthetic event at the call site. P81-132768.
  const applySearch = (value: string) =>
    handleFilterInput({
      target: { value }
    } as unknown as React.ChangeEvent<HTMLInputElement>);

  const handleFilterButton = () => {
    setFilterButton(!filterButton);
  };

  const onClearSearch = () => {
    setFilter({ ...filter, search: '' });
    setStartFilter(!startFilter);
  };

  // Tool bar props
  const handleTableSettingsMenuItemClick: ToolbarProps['handleTableSettingsMenuItemClick'] =
    useCallback(
      value => () => {
        let selectedCount = 0;
        const mapped = tableSettingsListItems.map(item => {
          const newItem =
            item.name === value
              ? {
                  ...item,
                  selected: !item.selected
                }
              : item;

          if (newItem.selected) {
            selectedCount += 1;
          }

          return newItem;
        });
        const result = mapped!.map(item => ({
          ...item,
          checkboxProps: {
            ...item.checkboxProps,
            disabled: selectedCount < 5 ? !!item.selected : false
          }
        }));
        setTableSettingsListItems(result);
        setTableSettingsDisplay(result);
      },
      [tableSettingsListItems]
    );

  const handleSearchChangeCallback: ToolbarProps['handleSearchChangeCallback'] =
    useCallback(
      value => {
        const result = tableSettingsListItems.filter(item =>
          item.name.toString().toLowerCase().includes(value)
        );
        setTableSettingsDisplay(result);
      },
      [tableSettingsListItems]
    );

  const handleChangeNumOfRows: DropDownMenuProps['handleClick'] =
    value => () => {
      setNumOfRowsOptions(
        numOfRowsOptions?.map(item => {
          if (String(item.name) === String(value)) {
            return {
              ...item,
              selected: true
            };
          }
          return {
            ...item,
            selected: false
          };
        })
      );
      setFilter({
        ...filter,
        itemsPerPage: value as number,
        currentPage: 1
      });
      setStartFilter(!startFilter);
    };

  const onSortEnd: ToolbarProps['onSortEnd'] = args => {
    const { oldIndex, newIndex } = args;
    if (oldIndex !== newIndex) {
      const newArray = arrayMove(tableSettingsListItems, oldIndex, newIndex);
      setTableSettingsListItems(newArray);
      setTableSettingsDisplay(newArray);
    }
  };
  const rowsPerPage = useMemo(
    () =>
      Number(numOfRowsOptions.find(item => item.selected === true)?.name) || 0,
    [numOfRowsOptions]
  );

  const rowSelectionChanged = useMemo(
    () => filter?.itemsPerPage !== rowsPerPage,
    [filter?.itemsPerPage, rowsPerPage]
  );

  const tableSettingsChanged = useMemo(
    () => !isEqual(originalTableSettingsListItems, tableSettingsDisplay),
    [originalTableSettingsListItems, tableSettingsDisplay]
  );

  // Pagination props

  const changeCurrentPage = (page: number) => {
    setFilter({ ...filter, currentPage: page });
    setStartFilter(!startFilter);
  };

  return {
    tableConfigDisplay,
    handleTableSettingsMenuItemClick,
    handleSearchChangeCallback,
    handleChangeNumOfRows,
    setFilter,
    filter,
    onSortEnd,
    rowSelectionChanged,
    tableSettingsDisplay,
    changeCurrentPage,
    numOfRowsOptions,
    tableSettingsChanged,
    results,
    setResults,
    tableData,
    setTableData,
    loaderTable,
    setLoaderTable,
    direction,
    setDirection,
    startFilter,
    setStartFilter,
    handleFilterInput,
    applySearch,
    handleFilterButton,
    filterButton,
    setFilterButton,
    onClearSearch,
    filterBackend,
    setFilterBackend,
    visibleFilterSave,
    setVisibleFilterSave,
    visibleFilterEdit,
    setVisibleFilterEdit,
    changeFilter,
    setChangeFilter,
    trigerResetDatePicker,
    setTrigerResetDatePicker,
    tableConf,
    setTableConf,
    filters,
    setFilters,
    loader,
    setLoader,
    dataConection,
    setDataConection,
    selectLoader,
    setSelectLoader,
    prevStateFilter,
    setPrevStateFilter,
    prevStateTableConf,
    setPrevStateTableConf,
    triggerSaveTableSettings,
    triggerStartEffect,
    setTriggerStartEffect
  };
};
